/**
 * Realtime UI Action Executor (P0-4 Phase 2 — CRITICAL-3 + CRITICAL-4)
 *
 * Defense-in-depth: this is Layer 0 (frontend), BEFORE backend Layer 1
 * (`dispatch_tool_call` whitelist) + Layer 2 (router re-check). All three
 * layers MUST be kept in sync, else attention_guide (or any rejected tool)
 * could slip through if backend Layer 1 regresses.
 *
 * C4 sub-decisions (4 of 4 enforced here):
 *   (4a) POST-then-apply sequential order: await audit POST → 200 → THEN apply.
 *        No Promise.all (would create fire-and-forget UI on POST failure).
 *   (4b) 5s timeout → NO apply (defensive, see TOOL_DISPATCH_TIMEOUT_MS).
 *   (4c) 401 → trigger hook re-connect + skip apply (token expired).
 *   (4d) `call_id` dedup map (1s window, cap 100 — memory leak guard).
 *
 * M2 (cycle 2 from 门下省): `onApplyOverlay` MUST be invoked with ARRAY
 * signature `[{ type, ...args }]` to match `Dashboard.js:79
 * handleDashboardUiActions = (actions) => {...}` which calls
 * `actions.some((a) => ELEMENT_TARGETING_TYPES.includes(a.type))` (array.some
 * on object would throw).
 */

import api from './api';
import {
  REALTIME_ALLOWED_ACTION_TYPES_FRONTEND,
  TOOL_DISPATCH_TIMEOUT_MS,
  TOOL_DISPATCH_DEDUP_WINDOW_MS,
  TOOL_DISPATCH_DEDUP_CAP,
} from './realtimeVoiceConstants';

/**
 * @param {Object} deps
 * @param {(actions: Array<{type: string}>) => void} deps.onApplyOverlay
 *   Dashboard.js:79 `handleDashboardUiActions` (array signature).
 * @param {() => void} deps.onAuthRetry
 *   Hook callback to disconnect + reconnect on 401 (token expired).
 * @param {{ current: string|null }} deps.realtimeSessionIdRef
 *   useRef holding current realtime session id (server-side resource).
 * @returns {(call: { callId?: string, toolName: string, args: Object }) => Promise<Object>}
 *   Resolves with one of:
 *     { dedup: true } | { layer0Reject: true } | { timeout: true }
 *     | { authExpired: true } | { applied: true, result: Object }
 */
export function createUiActionExecutor({ onApplyOverlay, onAuthRetry, realtimeSessionIdRef }) {
  // C4 sub-decision (4d): per-executor dedup map.
  // Cleared on hook unmount (executor garbage-collected with hook closure).
  const dedupMap = new Map();

  function pruneDedup() {
    const cutoff = Date.now() - TOOL_DISPATCH_DEDUP_WINDOW_MS;
    for (const [k, t] of dedupMap) {
      if (t < cutoff) dedupMap.delete(k);
    }
    // Hard cap: drop oldest until under cap.
    while (dedupMap.size > TOOL_DISPATCH_DEDUP_CAP) {
      const oldest = dedupMap.keys().next().value;
      dedupMap.delete(oldest);
    }
  }

  return async function execute({ callId, toolName, args }) {
    pruneDedup();

    // (4d) dedup BEFORE any side effect (audit POST included).
    if (callId && dedupMap.has(callId)) {
      console.warn('[realtimeUiActionExecutor] dedup skip', callId);
      return { dedup: true };
    }
    if (callId) {
      dedupMap.set(callId, Date.now());
    }

    // CRITICAL-3: Layer 0 frontend filter for apply_ui_action only.
    // Other tools (request_pedagogical_reasoning, submit_student_answer,
    // log_voice_turn, etc.) are validated by backend Layer 1 + 2.
    if (toolName === 'apply_ui_action') {
      const actionType = args?.action_type;
      if (!REALTIME_ALLOWED_ACTION_TYPES_FRONTEND.has(actionType)) {
        console.warn('[realtimeUiActionExecutor] Layer 0 reject', actionType);
        // Still POST to backend for audit trail (Layer 1 will reject again,
        // but the attempt is logged in RealtimeVoiceTrace).
        try {
          await api.post(
            '/api/realtime/tool',
            {
              realtime_session_id: realtimeSessionIdRef.current,
              tool_name: toolName,
              arguments: args,
            },
            { timeout: TOOL_DISPATCH_TIMEOUT_MS },
          );
        } catch (_) {
          // Swallow audit errors — Layer 0 reject is the primary signal.
        }
        return { layer0Reject: true };
      }
    }

    // (4a) POST-then-apply: audit POST FIRST, await success, THEN apply.
    let response;
    try {
      response = await api.post(
        '/api/realtime/tool',
        {
          realtime_session_id: realtimeSessionIdRef.current,
          tool_name: toolName,
          arguments: args,
        },
        { timeout: TOOL_DISPATCH_TIMEOUT_MS },
      );
    } catch (err) {
      // (4b) Timeout → NO apply (defensive).
      if (err.code === 'ECONNABORTED') {
        console.warn('[realtimeUiActionExecutor] dispatch timeout 5s no-apply', toolName);
        return { timeout: true };
      }
      // (4c) 401 → trigger reconnect + skip apply (token expired).
      if (err.response?.status === 401) {
        onAuthRetry();
        return { authExpired: true };
      }
      // Other errors: re-throw so hook can surface to UI if needed.
      throw err;
    }

    // Success path: only apply overlay for apply_ui_action.
    // M2: array signature `[{ type: action_type, ...args }]` for
    // Dashboard.js:79 `actions.some((a) => ...)`.
    //
    // e2e-completion post-merge hotfix P1-2: explicit destructure to drop
    // any caller-supplied `type` field BEFORE rebuilding the overlay action.
    // Spread-after-set (`{ type: args.action_type, ...args }`) lets a
    // malicious `args.type` (e.g. `attention_guide`) override the validated
    // `args.action_type` — bypassing the Layer 0 frontend filter and the
    // backend Layer 3 subset gate. Discarding `type` here forces the
    // overlay payload to use ONLY the validated `action_type` as `type`.
    if (toolName === 'apply_ui_action' && response.data?.success && onApplyOverlay) {
      // eslint-disable-next-line no-unused-vars
      const { action_type, type: _ignored, ...payloadArgs } = args || {};
      onApplyOverlay([{ ...payloadArgs, type: action_type }]);
    }
    return { applied: true, result: response.data };
  };
}
